import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  TranslationService,
  User
} from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import {
  CustomOrderHistory,
  CustomOrderHistoryDetailSortOptions,
  CustomOrderHistoryEntry,
  CustomOrderHistoryUpdate
} from '../../custom-order-history.models';
import { CustomOrderHistoryService } from '../../custom-order-history.service';
import {
  backToOrderHistory,
  cancelOrder,
  cancelOrderEntry,
  cancelOrderEntryReset,
  cancelOrderReset,
  getOrderHistory,
  updateOrderEntries,
  updateOrderEntriesReset
} from '../../store/custom-order-history.actions';
import {
  errorCancelOrder,
  errorCancelOrderEntry,
  errorOrder,
  errorUpdateOrderEntries,
  loadingOrder,
  selectOrder,
  successCancelOrder,
  successCancelOrderEntry,
  successUpdateOrderEntries
} from '../../store/custom-order-history.selectors';
import {UserAccountFacade} from '@spartacus/user/account/root';

@Component({
    selector: 'app-custom-order-history-detail',
    templateUrl: './custom-order-history-detail.component.html',
    styleUrls: [
        './custom-order-history-detail.component.scss',
        './custom-order-history-detail-print.component.scss',
    ],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CustomOrderHistoryDetailComponent implements OnInit {

  uigOrderNumber: string;
  order$: Observable<CustomOrderHistory> = this.store.select(selectOrder);
  order: CustomOrderHistory;
  entries: CustomOrderHistoryEntry[];
  updatableEntries: CustomOrderHistoryEntry[];
  isLoadingOrder$: Observable<boolean> = this.store.select(loadingOrder);
  errorOrder$: Observable<Error> = this.store.select(errorOrder);

  isUpdateMode: boolean = false;

  isSuccessCancelOrder$: Observable<boolean> = this.store.select(
    successCancelOrder
  );
  errorCancelOrder$: Observable<boolean> = this.store.select(errorCancelOrder);

  isSuccessCancelOrderEntry$: Observable<boolean> = this.store.select(
    successCancelOrderEntry
  );
  errorCancelOrderEntry$: Observable<boolean> = this.store.select(
    errorCancelOrderEntry
  );

  isSuccessUpdateOrderEntries$: Observable<boolean> = this.store.select(
    successUpdateOrderEntries
  );
  errorUpdateOrderEntries$: Observable<boolean> = this.store.select(
    errorUpdateOrderEntries
  );

  selectedSortOption: string;
  sortOptions: string[];
  sortDirection: string = 'asc';
  compare = (v1: string | number | Date, v2: string | number | Date) =>
    v1 < v2 ? -1 : v1 > v2 ? 1 : 0;

  @ViewChild('f') updateForm: NgForm;
  CAN_EDIT = 'edit_order_history';
  canEdit: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private routing: RoutingService,
    private translation: TranslationService,
    private messageService: GlobalMessageService,
    private modalService: NgbModal,
    private customOrderHistoryService: CustomOrderHistoryService,
    private store: Store<{ order: CustomOrderHistory }>,
    private userAccountFacade: UserAccountFacade
  ) {}

  ngOnInit(): void {
    this.userAccountFacade.get()
    .pipe(filter((user: User) => user && Object.keys(user).length > 0))
    .subscribe((user) => {
      const hasRole = user.roles && user.roles.includes(this.CAN_EDIT);
      if (hasRole) {
        this.canEdit = true;
      }
    });
    this.setSortOptions();
    this.uigOrderNumber = this.route.snapshot.paramMap.get('orderCode');
    this.order$
      .pipe(
        filter((order) => order != null),
        tap((order) => {
          this.order = order;
          this.entries = order.entries;
          this.onChangeSort();
          this.checkForUpdatableEntries(this.entries);
        })
      )
      .subscribe();
    this.errorOrder$
      .pipe(
        filter((error) => error != null),
        switchMap(() => this.translation.translate('customOrderHistory.errorOrder')),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
        })
      )
      .subscribe();
    this.errorCancelOrder$
      .pipe(
        filter((error) => error),
        switchMap(() =>
          this.translation.translate('customOrderHistory.errorCancelOrder')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(cancelOrderReset());
        })
      )
      .subscribe();
    this.isSuccessCancelOrder$
      .pipe(
        filter((success) => success),
        switchMap(() =>
          this.translation.translate('customOrderHistory.successCancelOrder')
        ),
        tap((text) => {
          this.messageService.add(
            text,
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.store.dispatch(cancelOrderReset());
          this.goToOrderOverview();
        })
      )
      .subscribe();
    this.errorCancelOrderEntry$
      .pipe(
        filter((error) => error),
        switchMap(() =>
          this.translation.translate('customOrderHistory.errorCancelOrderEntry')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(cancelOrderEntryReset());
        })
      )
      .subscribe();
    this.isSuccessCancelOrderEntry$
      .pipe(
        filter((success) => success),
        switchMap(() =>
          this.translation.translate('customOrderHistory.successCancelOrderEntry')
        ),
        tap((text) => {
          this.messageService.add(
            text,
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.store.dispatch(cancelOrderEntryReset());
          this.fetchOrder();
        })
      )
      .subscribe();
    this.errorCancelOrderEntry$
      .pipe(
        filter((error) => error),
        switchMap(() =>
          this.translation.translate('customOrderHistory.errorCancelOrderEntry')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(cancelOrderEntryReset());
        })
      )
      .subscribe();
    this.isSuccessUpdateOrderEntries$
      .pipe(
        filter((success) => success),
        switchMap(() =>
          this.translation.translate('customOrderHistory.successUpdateOrderEntries')
        ),
        tap((text) => {
          this.messageService.add(
            text,
            GlobalMessageType.MSG_TYPE_CONFIRMATION
          );
          this.store.dispatch(updateOrderEntriesReset());
          this.fetchOrder();
        })
      )
      .subscribe();
    this.errorUpdateOrderEntries$
      .pipe(
        filter((error) => error),
        switchMap(() =>
          this.translation.translate('customOrderHistory.errorUpdateOrderEntries')
        ),
        tap((text) => {
          this.messageService.add(text, GlobalMessageType.MSG_TYPE_ERROR);
          this.store.dispatch(updateOrderEntriesReset());
        })
      )
      .subscribe();
    this.fetchOrder();
  }

  setSortOptions() {
    this.selectedSortOption =
      CustomOrderHistoryDetailSortOptions[
        CustomOrderHistoryDetailSortOptions.position
      ];
    this.sortOptions = Object.values(
      CustomOrderHistoryDetailSortOptions
    ).filter((value) => typeof value === 'string') as string[];
  }

  onChangeSort() {
    this.order = { ...this.order };
    if(this.order?.entries){
      this.entries = [...this.order.entries].sort((a, b) => {
        const res = this.compare(
          a[this.selectedSortOption],
          b[this.selectedSortOption]
        );
        return this.sortDirection === 'asc' ? res : -res;
      });
    }
    else {
      this.entries = [];
    }
    this.order.entries = this.entries;
  }

  onSubmit(updatePosition: number) {
    var count = this.updatableEntries.length;
    var entries = new Array();
    for (let i = 0; i < count; i++) {
      let position = this.entries[i].position;
      let quantity = this.updateForm.controls['deliveredQuantityNew[' + i + ']']
        .value;
      entries.push({ position, quantity });
    }

    var formData = {} as CustomOrderHistoryUpdate;
    formData.orderNumber = this.uigOrderNumber;
    formData.updatePosition = updatePosition;
    formData.entries = entries;

    this.store.dispatch(
      updateOrderEntries({
        formData,
      })
    );

    this.updatableEntries = [];
  }

  onUpdateMode() {
    this.isUpdateMode = true;
    this.entries = this.updatableEntries;
  }

  onCancelOrder(confirmationMessage) {
    this.modalService.open(confirmationMessage, { centered: true }).result.then(
      (result) => {
        this.store.dispatch(
          cancelOrder({
            uigOrderNumber: this.uigOrderNumber,
          })
        );
      },
      (reason) => {}
    );
  }

  onCancelOrderEntry(position: string, confirmationMessage) {
    this.modalService.open(confirmationMessage, { centered: true }).result.then(
      (result) => {
        this.store.dispatch(
          cancelOrderEntry({
            uigOrderNumber: this.uigOrderNumber,
            position,
          })
        );
      },
      (reason) => {}
    );
  }

  onExport() {
    this.customOrderHistoryService.printOrExportOrder(this.uigOrderNumber);
  }

  fetchOrder(): void {
    this.isUpdateMode = false;
    this.store.dispatch(
      getOrderHistory({
        uigOrderNumber: this.uigOrderNumber,
      })
    );
  }

  goToOrderOverview(): void {
    this.store.dispatch(backToOrderHistory());
    this.routing.go({
      cxRoute: 'orders',
    });
  }

  checkForUpdatableEntries(entries: CustomOrderHistoryEntry[]): void {
    this.updatableEntries = [];
    for(var i = 0; i<entries.length; i++) {
      if(entries[i].cancelable == true && entries[i].noChange == false)
      {
        this.updatableEntries.push(entries[i]);
      }
    }
  }
}
